import { ErrorHandler, Injectable, inject, NgZone } from '@angular/core';
import { LoggerService } from './logger.service';

/**
 * Global error handler that catches *all* unhandled errors in the Angular app
 * and ships them as structured logs to Grafana/Loki.
 *
 * Also listens for unhandled Promise rejections on `window`.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(LoggerService);
  private readonly ngZone = inject(NgZone);

  /** Attach window‑level listeners (call once at bootstrap). */
  init(): void {
    if (typeof window === 'undefined') return;

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      this.ngZone.run(() => {
        this.logger.error(
          'GlobalErrorHandler',
          `Unhandled Promise rejection: ${event.reason}`,
          event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
          {
            type: 'unhandledRejection',
            promise: String(event.promise),
          },
        );
      });
    });

    // Uncaught errors (should also be caught by Angular, but belt + suspenders)
    window.addEventListener('error', (event: ErrorEvent) => {
      this.ngZone.run(() => {
        // Skip errors that are just resource-loading failures without messages
        if (!event.message && event.target && event.target !== window) {
          this.logger.warn(
            'GlobalErrorHandler',
            `Resource load error: ${(event.target as any)?.src ?? (event.target as any)?.href ?? 'unknown'}`,
            {
              type: 'resourceError',
              target: String(event.target),
            },
          );
          return;
        }

        this.logger.error(
          'GlobalErrorHandler',
          event.message || 'Uncaught error',
          event.error,
          {
            type: 'uncaughtError',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        );
      });
    });
  }

  /** Angular ErrorHandler implementation. */
  handleError(error: Error): void {
    this.logger.error(
      'GlobalErrorHandler',
      `Angular error: ${error.message}`,
      error,
      {
        type: 'angularError',
        errorName: error.name,
      },
    );

    // Re‑throw in dev so the console still shows Angular's default detail
    // (the logger already wrote to console, but this keeps native behaviour)
    if (typeof window !== 'undefined' && !(window as any).__zone_symbol__UNPATCHED_EVENTS) {
      console.error('Angular Error (re-thrown):', error);
    }
  }
}
